import {settings} from "@/main"
import * as generated from './api-client'
import _ from "underscore";
import Loader from "@/features/shared/components/Loader";
import {authService, profileIdStorageKey} from "@/AuthService";
import {cultureService} from "@/CultureService";
import toasts from "@/components/Toasts";

/* eslint-disable */
// Don't know why it suddenly started complaining about this, but let's disable checking for now.
export namespace factory {
    export function create<T>(type: { new(): T }): T {
    const obj = new type() as any;
    obj.init({});
    return obj;
  }

   export function empty<T>(type: { new(): T }): T {
    const obj = new type() as any;
    return obj;
  }
}
/* eslint-enable */

export class ClientBase {
    private loader = new Loader();
    private handleNotFound = true;

    getBaseUrl(defaultUrl: string, baseUrl?: string) {
        return settings.misApiBaseUrl;
    }

    setLoader(loader:Loader){
        this.loader = loader;
        return this;
    }

    ignoreNotFound(){
        this.handleNotFound = false;
        return this;
    }

    protected transformOptions(options: RequestInit) : Promise<RequestInit> {
        this.loader.startLoading();

        const headers = new Headers(options.headers);
        if (authService?.isLoggedIn) {
            headers.set("Authorization", authService.getAuthorizationHeaderValue());

            const profileId = sessionStorage.getItem(profileIdStorageKey);
            if (profileId)
                headers.set(profileIdStorageKey, profileId);
        }

        headers.set("Accept-Language", cultureService.currentCulture)

        options.headers = headers;
        try {
            return Promise.resolve(options);
        } catch (e) {
            this.loader.endLoading();
            throw e;
        }
    }

    protected transformResult(url: string, response: Response, handleResponse: (response: Response) => any) {
        this.loader.endLoading();

        if (response.status == 404) {
            if (!this.handleNotFound)
                return Promise.reject(response);
        }

        return handleResponse(response);
    }
}

// NOTE: When you want to extend classes, add the extended class name to api-client.nswag "extendedClasses" property.

export class ResultError extends generated.ResultError{
    // Keys that have their messages bound to an input field. These messages are shown below the input fields.
    // The binding is defined as retrieving a message via a specific method defined in this.
    readonly handledKeys = new Set();

    get isEmpty(){
        return !this.hasMessages();
    }

    hasMessages(key: string | null = null): boolean {
        return key ? _.keys(this.messages).includes(key) : _.keys(this.messages).length > 0;
    }

    getAndSetAsHandled(key: string): string[] {
        if (this.hasMessages(key)) {
            this.handledKeys.add(key)
            return this.messages[key];
        } else {
            return [];
        }
    }

    findAndSetAsHandled(approximateKey: string): string[] {
        const matchedKeys: string[] = _.keys(this.messages).filter(key => key.includes(approximateKey));
        const messages = new Set<string>(); // no need to show duplicate messages

        for (const matchedKey of matchedKeys) {
            messages.add(this.messages[matchedKey].join('. '));
            this.handledKeys.add(matchedKey);
        }

        return Array.from(messages);
    }

    get messagesFlattened() {
        return _.flatten(_.values(this.messages));
    }

    getNotHandledMessages(): string[] {
        const notHandledKeys: string[] = [];
        for (const key of _.keys(this.messages)) {
            if (!this.handledKeys.has(key)) {
                notHandledKeys.push(key);
            }
        }

        const notHandledMessages: string[] = [];
        for (const key of notHandledKeys) {
            notHandledMessages.push(this.messages[key].join('. '));
        }

        return notHandledMessages;
    }

    // Need to wait with this.$nextTick() until DOM is updated and any error messages are displayed below input fields
    // NOTE: This is not meant to be used directly. Use Vue component's setValidationErrors instead.
    showToastsIfNecessary(nextTick: (callback: Function) => void, toastTextIfHasAnyHandledMessages: string | null) {
        nextTick(() => {
            if (toastTextIfHasAnyHandledMessages && this.handledKeys.size > 0) {
                toasts.failure(toastTextIfHasAnyHandledMessages);
            }

            for (const message of this.getNotHandledMessages()) {
                toasts.failure(message, 0);
            }
        });
    }

    appendMessage(message: string, key = ""){
        if(key === "") {
            this.messages = { ...this.messages, [key]: [message] };
            return ResultError.fromJS(this);
        }
        if (this.messages[key]) {
            this.messages[key].push(message); // Vue's reactivity works with Array.push.
        } else {
            this.messages = { ...this.messages, [key]: [message] }; // Vue's reactivity does not work when adding new key to an object, so we trigger reactivity by replacing whole object.
        }
        return ResultError.fromJS(this);
    }

    clearKey(key:string) {
        if(!this.messages) return;
        delete this.messages[key];
        this.handledKeys.delete(key);
    }

    clear(){
        this.messages = {};
        this.handledKeys.clear();
    }

    static createEmpty(){
        return factory.create(ResultError);
    }
}


